<template>
  <v-app>
    <SidebarRider @open-notification="openNotification" @logout="handleLogout" />

    <v-main>
      <ActiveTravelBanner role="rider" />
      <!-- Extra bottom padding on mobile so content isn't hidden behind the bottom nav -->
      <div class="rider-content" :class="{ 'rider-content--mobile': $vuetify.breakpoint.smAndDown }">
        <router-view />
      </div>
    </v-main>

    <RiderFloatingChat />
  </v-app>
</template>

<script>
import SidebarRider from "@/components/SidebarRider.vue";
import RiderFloatingChat from "@/components/Chat/RiderFloatingChat.vue";
import ActiveTravelBanner from "@/components/Shared/ActiveTravelBanner.vue";
import { logoutApi } from "@/services/api/auth";
import { connectUserSocket, disconnectUserSocket } from "@/plugins/socket";

export default {
  name: "DefaultRider",
  components: { SidebarRider, RiderFloatingChat, ActiveTravelBanner },

  data() {
    return {
      isOnline: false,
      riderId: null,
      locationWatchId: null,
      locationHeartbeatId: null,
      lastKnownLocation: null, // เก็บพิกัดล่าสุดไว้ที่นี่
    };
  },

  mounted() {
    this.fetchInitialStatus();
    // Listen for online status changes from child components
    this.$root.$on("rider:update-online-status", (status) => {
      this.isOnline = status;
    });

    // ให้หน้า Child ร้องขอพิกัดล่าสุดได้ทันที (ไม่ต้องรอขยับ)
    this.$root.$on("rider:request-current-location", () => {
      if (this.lastKnownLocation) {
        this.$root.$emit("rider:location-updated", this.lastKnownLocation);
      }
    });
  },

  beforeDestroy() {
    this.stopLocationTracking();
    this.$root.$off("rider:update-online-status");
    this.$root.$off("rider:request-current-location");
  },

  watch: {
    isOnline(newVal) {
      if (newVal) {
        this.startLocationTracking();
      } else {
        this.stopLocationTracking();
      }
    }
  },

  methods: {
    async fetchInitialStatus() {
      try {
        const response = await this.$axios.get(`${this.$domain_api}/api/v1/auth/rider/dashboard`, {
          headers: { Authorization: `Bearer ${this.$store.getters.accessToken}` }
        });
        if (response.data.success) {
          const { rider } = response.data.data;
          this.isOnline = rider.isOnline;
          this.riderId = rider.id;
          connectUserSocket(rider.id, "rider");

          if (this.isOnline) {
            this.startLocationTracking();
          }
        }
      } catch (error) {
        console.error("Fetch initial rider status error:", error);
      }
    },

    startLocationTracking() {
      if (!navigator.geolocation) {
        console.error("Geolocation is not supported by this browser.");
        return;
      }
      this.stopLocationTracking();

      const onPosition = (position) => {
        const { latitude, longitude } = position.coords;
        this.lastKnownLocation = { lat: latitude, lng: longitude };
        this.$root.$emit("rider:location-updated", { lat: latitude, lng: longitude });
        if (this.$socket && this.riderId) {
          this.$socket.emit("rider:updateLocation", {
            riderId: this.riderId,
            latitude,
            longitude,
          });
        }
      };

      const onError = (error) => {
        console.error("Error watching position:", error);
        if (error.code === error.PERMISSION_DENIED) {
          this.$root.$emit("rider:location-error", "กรุณาสิทธิ์ให้แอปเข้าถึงตำแหน่งของคุณเพื่อรับงาน");
          this.isOnline = false;
        }
      };

      // watchPosition — fire เมื่อ GPS เคลื่อน
      this.locationWatchId = navigator.geolocation.watchPosition(onPosition, onError, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });

      // Heartbeat — บังคับส่งพิกัดทุก 10 วินาที แม้ไม่ขยับ
      this.locationHeartbeatId = setInterval(() => {
        navigator.geolocation.getCurrentPosition(onPosition, () => {
          // ถ้า GPS ไม่ตอบ ส่งพิกัดล่าสุดที่รู้แทน
          if (this.lastKnownLocation && this.$socket && this.riderId) {
            this.$socket.emit("rider:updateLocation", {
              riderId: this.riderId,
              latitude: this.lastKnownLocation.lat,
              longitude: this.lastKnownLocation.lng,
            });
          }
        }, { enableHighAccuracy: true, timeout: 5000, maximumAge: 10000 });
      }, 10000);
    },

    stopLocationTracking() {
      if (this.locationWatchId !== null) {
        navigator.geolocation.clearWatch(this.locationWatchId);
        this.locationWatchId = null;
      }
      if (this.locationHeartbeatId !== null) {
        clearInterval(this.locationHeartbeatId);
        this.locationHeartbeatId = null;
      }
    },

    openNotification() {},

    async handleLogout() {
      try {
        await logoutApi().catch(() => {});
      } catch (e) {
        console.error("Logout error:", e);
      } finally {
        disconnectUserSocket();
        if (this.$store && this.$store.dispatch) {
          await this.$store.dispatch("logout", { authGroup: "rider" }).catch(() => {});
        }
        sessionStorage.clear();
        localStorage.removeItem("rider_token");
        localStorage.removeItem("rider_role");
        this.$router.replace({ name: "login_rider" }).catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.rider-content {
  min-height: 100vh;
  background: #F1F5F9;
  padding: 0;
}

/* Push content up above the fixed 56px bottom nav bar + iOS safe area */
.rider-content--mobile {
  padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
}
</style>
